import { Injectable, inject, isDevMode, signal } from '@angular/core';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, firstValueFrom, tap } from 'rxjs';
import { BrowserNavigation, LoggerProvider } from '@shared';
import { LocalStorageProvider } from '../storage/local-storage.provider';
import { EidAuthStateAdapter } from './eid-auth-state.adapter';
import { AppConfigService } from '../../config/app-config.service';

/**
 * Type definitions for Enterprise Identity SDK
 */
interface EIDWindow extends Window {
  eid?: {
    configure: (config: EIDLegacyConfig | ((config: EIDModernConfig) => void)) => void;
  };
}

interface EIDLegacyConfig {
  sessionJwtResolver: () => string | null;
  postLogoutRedirectUri: string;
  logoutCallback: () => void;
  enableLogs: boolean;
}

interface EIDAuditingConfig {
  accessTokenCallback: () => string | null;
  productCode: string;
}

interface EIDModernConfig {
  auditing: (config: EIDAuditingConfig) => void;
}

/**
 * Enterprise Identity OIDC Provider.
 * Facade for OidcSecurityService that simplifies authentication operations
 * and integrates with the Enterprise Identity SDK.
 */
@Injectable({ providedIn: 'root' })
export class EIDCProvider {
  private readonly _oidcSecurityService = inject(OidcSecurityService);
  private readonly _localStorage = inject(LocalStorageProvider);
  private readonly _authStateAdapter = inject(EidAuthStateAdapter);
  private readonly _logger = inject(LoggerProvider).createScoped('EIDC');
  private readonly _appConfigService = inject(AppConfigService);
  private readonly _browserNavigation = inject(BrowserNavigation);

  private readonly _isAuthenticated = signal<boolean>(false);
  private readonly _idToken = signal<string | null>(null);
  private readonly _accessToken = signal<string | null>(null);
  private readonly _userData = signal<unknown>(null);

  public readonly isAuthenticated = this._isAuthenticated.asReadonly();
  public readonly idToken = this._idToken.asReadonly();
  public readonly accessToken = this._accessToken.asReadonly();
  public readonly userData = this._userData.asReadonly();

  /**
   * Signs in the user using OIDC.
   * If not authenticated, initiates the authorization flow.
   * If authenticated, stores user data locally.
   */
  public async signIn(): Promise<void> {
    const oidcObservable = this._checkAuth();
    const eidResponse = await firstValueFrom(oidcObservable);

    this._logger.info('Authentication check completed');

    if (!eidResponse.isAuthenticated) {
      this._logger.info('User not authenticated, initiating OIDC authorization');
      this._authorize();
      this.signOutLocal();
      
    } else {
      this._logger.info('User authenticated successfully');
      this._storeAuthData(eidResponse);
    }
  }

  /**
   * Initializes the session by signing in and configuring the EID SDK.
   * This method should be called during application initialization.
   */
  public async initSession(): Promise<void> {
    try {
      await this.signIn();

      if (!this._isAuthenticated()) {
        this._logger.warn('Session initialization skipped - user not authenticated');
        return;
      }

      this._logger.info('Session initialized successfully', {
        isAuthenticated: this._isAuthenticated(),
        hasAccessToken: !!this._accessToken(),
        hasIdToken: !!this._idToken(),
      });

      // Allow time for authentication state to settle before configuring SDK
      setTimeout(() => {
        this._configureLegacySDK();
        this._configureModernSDK();
      }, 3000);
    } catch (error) {
      this._logger.error('Session initialization failed', error);
      this.signOutLocal();
    }
  }

  /**
   * Signs out the user locally by clearing stored authentication data.
   */
  public signOutLocal(): void {
    this._isAuthenticated.set(false);
    this._idToken.set(null);
    this._accessToken.set(null);
    this._userData.set(null);
    this._localStorage.clear();
    this._authStateAdapter.clearAuthState();
    this._logger.info('Local sign out completed');
  }

  /**
   * Signs out the user from both the application and identity provider.
   */
  public signOut(): void {
    const baseAuthUrl = this._appConfigService.appSwitcherBaseUrl;
    const endSessionUrl = `${baseAuthUrl}/connect/endsession?id_token_hint=${this._idToken()}`;
    this._oidcSecurityService.logoffAndRevokeTokens().pipe(
      tap(() => this.signOutLocal())
    ).subscribe(() => this._browserNavigation.navigateToUrl(endSessionUrl));
  }

  /**
   * Checks the authentication status.
   * @returns Observable of LoginResponse containing authentication state
   */
  private _checkAuth(): Observable<LoginResponse> {
    return this._oidcSecurityService.checkAuth();
  }

  /**
   * Initiates the authorization flow.
   * Redirects the user to the identity provider's login page.
   */
  private _authorize(): void {
    this._oidcSecurityService.authorize();
  }

  /**
   * Stores authentication data from the OIDC response.
   * Stores user data under 'hcmi_loggedInUser' key (matching BackgroundSolutions' 'bsi_loggedInUser' pattern).
   * @param response - The LoginResponse from OIDC authentication
   */
  private _storeAuthData(response: LoginResponse): void {
    this._isAuthenticated.set(response.isAuthenticated);
    this._idToken.set(response.idToken);
    this._accessToken.set(response.accessToken);
    this._userData.set(response.userData);

    // Store user data in localStorage (matching BackgroundSolutions pattern with bsi_loggedInUser)
    const userData = response.userData as Record<string, string> | null;
    if (userData) {
      const loggedInUser = {
        firstName: userData['given_name'] || '',
        lastName: userData['family_name'] || '',
        email: userData['email'] || '',
        globalEmployerId: userData['global_tnt'] || '',
        globalUserId: userData['global_sub'] || '',
        coredataUserId: userData['coredata_user_id'] || '',
        isActive: true,
        productCode: 'HCMI',
        isSuperAdmin: userData['act'] != null,
      };
      localStorage.setItem('hcmi_loggedInUser', JSON.stringify(loggedInUser));
    }

    // Bridge into application auth state
    this._authStateAdapter.bridgeAuthState(response);
  }

  /**
   * Configures the legacy Enterprise Identity SDK.
   * This configuration supports older SDK APIs that use JWT resolvers.
   */
  private _configureLegacySDK(): void {
    try {
      (window as EIDWindow).eid?.configure({
        sessionJwtResolver: () => this._idToken(),
        postLogoutRedirectUri: window.location.origin,
        logoutCallback: () => this.signOutLocal(),
        enableLogs: isDevMode(),
      });
      this._logger.info('Legacy EID SDK configured');
    } catch (error) {
      this._logger.error('Failed to configure legacy EID SDK', error);
    }
  }

  /**
   * Configures the modern Enterprise Identity SDK.
   * This configuration supports newer SDK features like auditing.
   */
  private _configureModernSDK(): void {
    try {
      (window as EIDWindow).eid?.configure((config: EIDModernConfig) => {
        config.auditing({
          // Remove accessTokenCallback once OpenIdConnect is implemented in SDK
          accessTokenCallback: () => this._accessToken(),
          productCode: 'HCMI',
        });
      });
      this._logger.info('Modern EID SDK configured');
    } catch (error) {
      this._logger.error('Failed to configure modern EID SDK', error);
    }
  }
}
